import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { AlarmClock, LogIn, LogOut, TriangleAlert } from "lucide-react";

import { cn } from "../lib/utils.js";
import { AsyncButton } from "./ui/async-button.js";
import { Badge } from "./ui/badge.js";
import { useCloseOnOutsideClick, DropdownPanel } from "./DropdownButton.js";
import { useTodayAttendance } from "../hooks/useTodayAttendance.js";
import { useMe } from "../context/MeContext.js";

function fmtTime(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

const NOTE_LINK = "font-semibold text-brand-600 hover:underline";

/** A warning line under the buttons: something the employee has to act on. */
function PanelNote({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-start gap-2 border-t border-surface-200 px-3 py-2.5 text-[12px] text-surface-700">
      <TriangleAlert size={14} className="mt-0.5 shrink-0 text-[var(--color-warning-fg)]" />
      <span>{children}</span>
    </div>
  );
}

/** "14 Oct" from a "YYYY-MM-DD" day. */
function fmtDay(day: string): string {
  return new Date(`${day}T00:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });
}

/** "6h 42m" between check-in and now (or check-out). */
function elapsedLabel(from: string, to: string | null): string {
  const mins = Math.max(0, Math.round((new Date(to ?? Date.now()).getTime() - new Date(from).getTime()) / 60000));
  return `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, "0")}m`;
}

/**
 * Top-bar attendance clock: an alarm icon that opens a small card with Check In
 * and Check Out. Available to every employee, alongside the notification bell.
 */
export interface AttendanceClockProps {
  /** Width class, shared with the notification panel so the two match. */
  panelClassName?: string;
}

export function AttendanceClock({ panelClassName }: AttendanceClockProps) {
  const { record, loading, isOnTheClock, missedCheckoutDate, hasShift, canCheckIn, canCheckOut, checkIn, checkOut, refresh } = useTodayAttendance();
  const { attendanceApprovalsEnabled } = useMe();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useCloseOnOutsideClick(ref, open, () => setOpen(false));

  // Opening is the moment the numbers are read, so re-check then — covers a
  // check-in made in another tab, or a record changed server-side.
  useEffect(() => { if (open) void refresh(); }, [open, refresh]);

  // Re-render once a minute so the running total stays honest while open.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!isOnTheClock || !open) return;
    const id = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, [isOnTheClock, open]);

  // The icon rings only while a check-in is outstanding; once they are on the
  // clock it settles and reacts to hover alone.
  const needsAction = !loading && canCheckIn && !isOnTheClock;

  const status = loading ? "Loading…"
    : isOnTheClock ? `On the clock since ${fmtTime(record!.checkIn)}`
    : missedCheckoutDate ? `Missed check-out on ${fmtDay(missedCheckoutDate)} (checked in ${fmtTime(record!.checkIn)})`
    : record?.isToday && record.checkOut ? `Checked out at ${fmtTime(record.checkOut)}`
    : "Not checked in today";

  return (
    // No `relative` here on purpose: the header group is the positioning context
    // for this panel and the notification one, so they share a right edge.
    <div ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title={status}
        aria-label={`Attendance — ${status}`}
        aria-expanded={open}
        className="relative flex h-9 w-9 items-center justify-center rounded-full text-surface-600 hover:bg-surface-100"
      >
        <AlarmClock
          size={18}
          className={cn(needsAction ? "alarm-rock text-[var(--color-error-fg)]" : "alarm-rock-hover",
                        isOnTheClock && "text-[var(--color-success-fg)]")}
        />
        {isOnTheClock && (
          <span
            aria-hidden="true"
            className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-surface-0 bg-success"
          />
        )}
      </button>

      {open && (
        // Offset and width come from the header's shared panel class; the
        // fallback matches it for any standalone use.
        <DropdownPanel align="end" glow="ultra" className={panelClassName ?? "mt-3 w-[340px]"}>
          <div className="border-b border-surface-200 px-3 py-2.5">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-semibold text-surface-900">Attendance</span>
              <Badge tone={isOnTheClock ? "success" : missedCheckoutDate ? "warning" : "neutral"}>
                {isOnTheClock ? "On the clock" : missedCheckoutDate ? "Missed check-out" : "Off the clock"}
              </Badge>
            </div>
            <p className="mt-1 text-[11px] text-surface-500">{status}</p>
          </div>

          <div className="grid grid-cols-2 gap-2 border-b border-surface-200 px-3 py-2.5 text-[11px]">
            <div>
              <div className="font-semibold uppercase tracking-[0.5px] text-surface-500">In</div>
              <div className="mt-0.5 text-[13px] font-bold text-surface-900">{fmtTime(record?.checkIn ?? null)}</div>
            </div>
            <div>
              <div className="font-semibold uppercase tracking-[0.5px] text-surface-500">
                {isOnTheClock ? "Elapsed" : "Out"}
              </div>
              {/* A running total only while they are still at work: on a missed
                  check-out it would count up for days (the 293h this replaced). */}
              <div className={cn("mt-0.5 text-[13px] font-bold", missedCheckoutDate ? "text-[var(--color-warning-fg)]" : "text-surface-900")}>
                {record?.checkOut ? fmtTime(record.checkOut)
                  : isOnTheClock && record?.checkIn ? elapsedLabel(record.checkIn, null)
                  : missedCheckoutDate ? "Missed"
                  : "—"}
              </div>
            </div>
          </div>

          {/* overflow-hidden sits here, not on the card: AsyncButton's shockwave
              expands past the button by design, and clipping it at the card
              would also cut off the card's own glow. */}
          <div className="flex gap-2 overflow-hidden p-3">
            {/* Variant and completion styling mirror the Attendance page's pair,
                so the same action looks the same wherever it is offered. */}
            <AsyncButton
              variant="success"
              doneLabel="Checked In"
              size="sm"
              radius="md"
              border="none"
              hoverBorder="accent"
              background="variant"
              completionIntensity="subtle"
              transitionDuration={150}
              progressPace={2000}
              className="flex-1"
              disabled={!canCheckIn || loading}
              // Stays open: the panel flips to "On the clock" and Check Out
              // becomes clickable straight away, without reopening it.
              onClick={() => checkIn()}
            >
              <LogIn size={14} className="mr-1.5" />Check In
            </AsyncButton>
            <AsyncButton
              variant="warning"
              doneLabel="Checked Out"
              size="sm"
              radius="md"
              border="none"
              hoverBorder="accent"
              background="variant"
              completionIntensity="subtle"
              transitionDuration={150}
              progressPace={2000}
              className="flex-1"
              disabled={!canCheckOut || loading}
              onClick={() => checkOut()}
            >
              <LogOut size={14} className="mr-1.5" />Check Out
            </AsyncButton>
          </div>

          {missedCheckoutDate && (
            <PanelNote>
              No check-out on {fmtDay(missedCheckoutDate)}, so that day has no hours yet.{" "}
              {attendanceApprovalsEnabled ? (
                <Link to={`/attendance?fix=${missedCheckoutDate}`} onClick={() => setOpen(false)} className={NOTE_LINK}>Fix it</Link>
              ) : "Ask HR to correct it."}
            </PanelNote>
          )}
          {/* The ask itself lives in ShiftNotSetBanner on the dashboard; this only says why Check In is grey. */}
          {!loading && !hasShift && (
            <PanelNote>
              No shift set, so you cannot check in yet.{" "}
              <Link to="/dashboard" onClick={() => setOpen(false)} className={NOTE_LINK}>Ask your manager to set it</Link>
            </PanelNote>
          )}
        </DropdownPanel>
      )}
    </div>
  );
}
